/**
 * Wordmark — the 3D-rendered "WEBNOVA" lockup.
 *
 * Renders the master wordmark file as a responsive `<picture>` element.
 * Replaces the previous CSS-typeset caps treatment because:
 *   - the rendered wordmark uses the same 3D extrusion + brass-edge
 *     lighting as the möbius mark, so they read as one designed pair
 *   - typeset CSS gold gradients always look flat next to a 3D mark
 *   - kerning was tuned at render time and shouldn't be relitigated
 *     by the browser's text-rendering on every resize
 *
 * The asset ships at 8.56:1 aspect (1155×135 alpha-cropped). Callers
 * specify a width and the height computes from that. For text-screen-
 * reader use the alt text is the literal brand name, not "WebNova
 * wordmark" — accessibility wants the text the visitor would speak.
 *
 * Three width variants exist in /public/brand/:
 *     320px  (~37px tall) — small UI use
 *     480px  (~56px tall) — nav default at 1x, small at 2x
 *     640px  (~75px tall) — hero / footer at hi-DPI
 *
 * The picture element serves WebP first (~60% smaller than PNG) with
 * PNG fallback for browsers that don't decode WebP.
 */
interface WordmarkProps {
  /** Rendered width in CSS pixels. Defaults to 160 (nav-sized). */
  width?: number;
  /** Image aria/alt context. Defaults to the brand name. */
  alt?: string;
  /** Extra className for layout positioning. */
  className?: string;
}

const ASPECT = 8.56; // width:height of the alpha-cropped source

export default function Wordmark({
  width = 160,
  alt = 'WebNova',
  className,
}: WordmarkProps) {
  const height = Math.round(width / ASPECT);

  return (
    <picture className={className}>
      <source
        type="image/webp"
        srcSet="/brand/webnova-wordmark-320.webp 1x, /brand/webnova-wordmark-640.webp 2x"
      />
      <img
        src="/brand/webnova-wordmark-320.png"
        srcSet="/brand/webnova-wordmark-320.png 1x, /brand/webnova-wordmark-640.png 2x"
        alt={alt}
        width={width}
        height={height}
        decoding="async"
        draggable={false}
        style={{ width, height, display: 'block' }}
      />
    </picture>
  );
}
